import { message } from 'antd'
import { ColumnProps } from 'antd/lib/table'
import { set, pick, isEmpty } from 'lodash'
import { useSelector, useDispatch } from 'react-redux'
import { useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { getUserIdentity } from '~/redux-modules/identity'
import isFlagEnabled from '~/lib/isFlagEnabled'
import logger from '~/lib/logger'
import { setUserPreferences } from '~/redux-modules/auth'
import { DefaultRootState } from '~/types'
import {
    InputMaybe,
    PreferenceType,
    SaveUserPreferencesMutationVariables,
    SqeAssignedToType,
    useAssignedSupplierQualityEngineersQuery,
    useSaveUserPreferencesMutation,
} from '~/gql/graphql'

import {
    DispatchMapType,
    FilterTabsType,
    GqlFilterLikeType,
    TableFilterType,
    TopSearchFilterKeysType,
    UrlFiltersType,
} from './types'
import {
    salesOrderByName_salesOrder_adminSalesOrderLines_processes as ProcessesNotes,
    salesOrderByName_salesOrder_processLines_materialConfiguration as MaterialsNotes,
} from './SalesOrder/SalesOrderDetails/__generated__/salesOrderByName'
import { NotesParameters } from './components/PartNotesModal/PartNotesModal'
import { PRODUCTION_TABS } from './Production/constants'

export const useShowAdditionalQualityFilterFlag = () => {
    const identity = useSelector(getUserIdentity)

    return isFlagEnabled(identity, 'SHOW_ADDITIONAL_QC_FILTERS', 'true')
}

export const useAddFollowersFlag = () => {
    const identity = useSelector(getUserIdentity)

    return isFlagEnabled(identity, 'USE_ADD_FOLLOWERS', 'true')
}

export function MutationOperationForPreference() {
    const [savePreferencesMutation] = useSaveUserPreferencesMutation()

    return {
        addUserPreference: (
            preferencesInput: SaveUserPreferencesMutationVariables,
        ) => {
            return savePreferencesMutation({
                variables: preferencesInput,
            })
                .then(result => result?.data?.updateOrAddUserPreference)
                .catch(error => {
                    logger.exception(error, {
                        extra: {
                            info: 'Failed to save user preferences.',
                            userId: preferencesInput.userId,
                        },
                    })
                    message.error('Failed to save preferences.')
                })
        },
    }
}

export const useSavePreferences = () => {
    const dispatch = useDispatch()
    const { id: userId } = useSelector(getUserIdentity)
    const mutationHandlerForPreference = MutationOperationForPreference()

    // eslint-disable-next-line comma-spacing
    const saveUserPreferences = async <T,>({
        columnsObject,
        properties,
        preferenceType,
        name,
    }: {
        columnsObject: ColumnProps<T>[]
        properties: string[]
        preferenceType: PreferenceType
        name: string
    }) => {
        const tablePreferences = {}

        columnsObject.forEach(column => {
            if (column.key) {
                set(tablePreferences, [column.key], pick(column, properties))
            }
        })

        const savedPrefs = await mutationHandlerForPreference.addUserPreference(
            {
                userId,
                preferenceType,
                name,
                value: tablePreferences,
            },
        )

        if (savedPrefs) {
            dispatch(setUserPreferences(savedPrefs))
        }
    }

    return saveUserPreferences
}

export const useShowJobRfqNumber = () => {
    const identity = useSelector(getUserIdentity)

    return isFlagEnabled(identity, 'SHOW_JOB_RFQ_NUMBER', 'true')
}

export const useShowMPAvailabilityFlag = () => {
    const identity = useSelector(getUserIdentity)

    return isFlagEnabled(identity, 'SHOW_MP_AVAILABILITY', 'true')
}
export const createUrlWithFilters = (
    newFilters: UrlFiltersType,
    dispatch: (
        filters: string,
        path?: string,
        nextTab?: FilterTabsType,
    ) => void,
    pathname?: string,
    nextTab?: FilterTabsType,
) => {
    const filtersUrl: string[] = []

    for (const key in newFilters) {
        const filterValue = newFilters[key].toString()

        filtersUrl.push(`${key}=${filterValue}`)
    }

    return dispatch(
        filtersUrl.length ? `?${filtersUrl.join('&')}` : '',
        pathname,
        nextTab,
    )
}

export const useUrlFilters = ({
    currentTab,
    dispatchMapsObject,
    encodeKeyMap,
    decodeKeyMap,
}: {
    currentTab: FilterTabsType
    dispatchMapsObject?: DispatchMapType
    encodeKeyMap?: Record<string, (value: string | number | boolean) => string>
    decodeKeyMap?: Record<string, (value: string) => string>
}) => {
    const { pathname, search } = useLocation()
    const navigate = useNavigate()

    const memoizedFilters = useMemo(() => {
        const filters: UrlFiltersType = {}

        if (search) {
            search
                .slice(1)
                .split('&')
                .forEach(filter => {
                    const urlFilter = decodeURIComponent(filter)
                    const urlFiltersArray = urlFilter.split('=')
                    const filterKey = urlFiltersArray[0]

                    const filterValue =
                        decodeKeyMap && filterKey in decodeKeyMap
                            ? [decodeKeyMap[filterKey](urlFiltersArray[1])]
                            : urlFiltersArray[1].split(',')

                    filters[filterKey] = filterValue
                })
        }
        return filters
    }, [decodeKeyMap, search])

    const dispatch = (
        filters: string,
        path?: string,
        nextTab?: FilterTabsType,
    ) => {
        const tab = nextTab || currentTab

        if (tab && dispatchMapsObject && dispatchMapsObject[tab]) {
            dispatchMapsObject[tab](filters)
        }

        navigate(`${path}${filters}`)
    }

    const setFilters = (
        newFilters?: TableFilterType,
        route?: string,
        remove?: boolean,
        nextTab?: FilterTabsType,
    ) => {
        if (!newFilters) {
            createUrlWithFilters({}, dispatch, pathname)
            return
        }
        const { filterName, filterValue } = newFilters

        if (!filterName.includes(currentTab)) {
            return
        }
        if (!filterValue?.length) {
            delete memoizedFilters[newFilters.filterName]
            createUrlWithFilters(memoizedFilters, dispatch, pathname)
            return
        }

        if (encodeKeyMap && filterName in encodeKeyMap) {
            filterValue[0] = encodeKeyMap[filterName](filterValue[0])
        }

        if (encodeKeyMap) {
            Object.keys(encodeKeyMap).forEach(filterKey => {
                if (memoizedFilters[filterKey]) {
                    memoizedFilters[filterKey] = [
                        encodeURIComponent(
                            memoizedFilters[
                                filterKey
                            ] as unknown as TopSearchFilterKeysType,
                        ),
                    ]
                }
            })
        }

        const updatedFilters = {
            ...(remove ? {} : memoizedFilters),
            [newFilters.filterName]: newFilters.filterValue,
        }

        createUrlWithFilters(
            updatedFilters,
            dispatch,
            route ? route : pathname,
            nextTab,
        )
    }

    const removeFilter = (listOfFiltersToBeRemoved: UrlFiltersType) => {
        Object.keys(listOfFiltersToBeRemoved).forEach(filterName => {
            delete memoizedFilters[filterName]
        })
        createUrlWithFilters(memoizedFilters, dispatch, pathname)
        return
    }

    return { filtersObject: memoizedFilters, setFilters, removeFilter }
}

// All query integration with filters has to be done with useFiltersGqlDocument - useFilters will be deprecated
export const useFilters = (
    filters: UrlFiltersType,
    mappingObject: {
        [x: string]: string
    },
    dateFilters?: string[],
) => {
    return useMemo(() => {
        if (dateFilters) {
            dateFilters.forEach(dateFilter => {
                const values = filters[dateFilter]

                if (values) {
                    filters[`${dateFilter}Start`] = [values[0]]
                    filters[`${dateFilter}End`] = [values[1]]
                    delete filters[dateFilter]
                }
            })
        }

        return Object.keys(filters).reduce(
            (
                accumulator: { [key: string]: (string | number)[] },
                currentValue,
            ) => {
                if (mappingObject[currentValue]) {
                    accumulator[mappingObject[currentValue]] =
                        filters[currentValue]
                }
                return accumulator
            },
            {},
        )
    }, [dateFilters, filters, mappingObject])
}

export const useFiltersGqlDocument = <
    GraphQlFilterType extends GqlFilterLikeType,
>(
    filters: Record<string, string[]>,
    mappingObject: Record<string, string>,
    dateFilters?: string[],
    numericFilters?: string[],
) => {
    return useMemo(() => {
        if (dateFilters) {
            dateFilters.forEach(dateFilter => {
                const values = filters[dateFilter]

                if (values) {
                    filters[`${dateFilter}Start`] = [values[0]]
                    filters[`${dateFilter}End`] = [values[1]]
                    delete filters[dateFilter]
                }
            })
        }

        return Object.keys(filters).reduce<GqlFilterLikeType>(
            (accumulator, currentValue) => {
                const filterName = mappingObject[currentValue]

                if (filterName) {
                    if (
                        ['DateStart', 'DateEnd', 'NameLike'].some(
                            filterSubstring =>
                                filterName.includes(filterSubstring),
                        )
                    ) {
                        accumulator[filterName] = filters[currentValue][0]
                    } else if (numericFilters?.includes(filterName)) {
                        accumulator[filterName] =
                            filters[currentValue].length === 1
                                ? Number(filters[currentValue][0])
                                : filters[currentValue]?.map(value =>
                                      Number(value),
                                  )
                    } else {
                        accumulator[filterName] = filters[currentValue]
                    }
                }
                return accumulator
            },
            {},
        ) as GraphQlFilterType
    }, [dateFilters, filters, mappingObject, numericFilters])
}

export const useAssignedSqes = (
    assignedTo: InputMaybe<SqeAssignedToType>,
    activeTab?: string,
) => {
    const { data } = useAssignedSupplierQualityEngineersQuery({
        variables: { assignedTo },
        skip: (activeTab && activeTab !== PRODUCTION_TABS.cars) || false,
    })
    const supplierQualityEngineers = data?.assignedSupplierQualityEngineers

    return supplierQualityEngineers?.map(supplierQualityEngineer => ({
        VALUE: supplierQualityEngineer.id.toString(),
        LABEL: supplierQualityEngineer.displayName,
    }))
}

export const useEnableAutoOtUI = () => {
    const identity = useSelector(getUserIdentity)

    return isFlagEnabled(identity, 'ENABLE_AUTO_OT_UI', 'true')
}

export const useEnablePhase2AutoOtUI = () => {
    const identity = useSelector(getUserIdentity)

    return isFlagEnabled(identity, 'ENABLE_AUTO_OT_UI_PHASE2_OPS', 'true')
}

export const useEnableMPWBPhase2AutoOtUI = () => {
    const identity = useSelector(getUserIdentity)

    return isFlagEnabled(identity, 'ENABLE_AUTO_OT_UI_MP_PHASE2', 'true')
}

export const formatProcessesPartNotes = (
    processes: ProcessesNotes[],
): NotesParameters[] => {
    if (!processes.length) {
        return []
    }
    const formatedProcessesPartNotesArray: NotesParameters[] = []

    processes.forEach(process => {
        const isGeneralNotesAvailable =
            process.dfmNotes?.process || process.processNotes
        const isAdditionSpecMetricsNotesAvailable = !isEmpty(
            process.additionalSpecifications,
        )
        const isDfmMetricsNotesAvailable =
            process.dfmNotes?.color ||
            process.dfmNotes?.texture ||
            process.dfmNotes?.thickness

        const isMetricsNotesAvailable =
            isAdditionSpecMetricsNotesAvailable || isDfmMetricsNotesAvailable

        if (isGeneralNotesAvailable) {
            formatedProcessesPartNotesArray.push({
                key: formatedProcessesPartNotesArray.length + 1,
                name: process.processName,
                additionalNotes: process.processNotes
                    ? process.processNotes
                    : '-',
                dfmNotes: process.dfmNotes?.process
                    ? process.dfmNotes?.process
                    : '-',
                rowSpan: isMetricsNotesAvailable ? 2 : 1,
            })
        }
        if (isMetricsNotesAvailable) {
            formatedProcessesPartNotesArray.push({
                key: formatedProcessesPartNotesArray.length + 1,
                name: process.processName,
                additionalNotes: isAdditionSpecMetricsNotesAvailable
                    ? {
                          color: {
                              value: process.color,
                              notes: process.additionalSpecifications?.color,
                          },
                          texture: {
                              value: process.texture,
                              notes: process.additionalSpecifications?.texture,
                          },
                          thickness: {
                              value: process.thickness,
                              notes: process.additionalSpecifications
                                  ?.thickness,
                          },
                      }
                    : '-',
                dfmNotes: isDfmMetricsNotesAvailable
                    ? {
                          color: {
                              value: null,
                              notes: process.dfmNotes?.color,
                          },
                          texture: {
                              value: null,
                              notes: process.dfmNotes?.texture,
                          },
                          thickness: {
                              value: null,
                              notes: process.dfmNotes?.thickness,
                          },
                      }
                    : '-',
                rowSpan: isMetricsNotesAvailable ? 0 : 1,
            })
        }
        if (!isGeneralNotesAvailable && !isMetricsNotesAvailable) {
            formatedProcessesPartNotesArray.push({
                key: formatedProcessesPartNotesArray.length + 1,
                name: process.processName,
                additionalNotes: '-',
                dfmNotes: '-',
                rowSpan: 1,
            })
        }
    })

    return formatedProcessesPartNotesArray
}

export const formatMaterialPartNotes = (
    materials: null | Pick<
        MaterialsNotes,
        | 'color'
        | 'name'
        | 'shoreValue'
        | 'additionalSpecifications'
        | 'dfmNotes'
    >,
): NotesParameters[] => {
    if (!materials || !materials?.name) {
        return []
    }
    const formatedMaterialPartNotesArray: NotesParameters[] = []

    const isAdditionSpecMetricsNotesAvailable =
        materials.additionalSpecifications?.color ||
        materials.additionalSpecifications?.shoreValue

    const isDfmMetricsNotesAvailable =
        materials.dfmNotes?.color || materials.dfmNotes?.shoreValue

    const isMetricsNotesAvailable =
        isAdditionSpecMetricsNotesAvailable || isDfmMetricsNotesAvailable

    const isGeneralMaterialNotesAvailable =
        materials.dfmNotes?.material ||
        materials.additionalSpecifications?.material

    if (!isGeneralMaterialNotesAvailable && !isMetricsNotesAvailable) {
        formatedMaterialPartNotesArray.push({
            key: formatedMaterialPartNotesArray.length + 1,
            name: materials.name ?? '',
            additionalNotes: '-',
            dfmNotes: '-',
            rowSpan: 1,
        })
        return formatedMaterialPartNotesArray
    }

    if (isGeneralMaterialNotesAvailable) {
        formatedMaterialPartNotesArray.push({
            key: formatedMaterialPartNotesArray.length + 1,
            name: materials.name ?? '',
            additionalNotes:
                materials.additionalSpecifications?.material ?? '-',
            dfmNotes: materials.dfmNotes?.material ?? '-',
            rowSpan: isMetricsNotesAvailable ? 2 : 1,
        })
    }
    if (isMetricsNotesAvailable) {
        formatedMaterialPartNotesArray.push({
            key: formatedMaterialPartNotesArray.length + 1,
            name: materials.name ?? '',
            additionalNotes: isAdditionSpecMetricsNotesAvailable
                ? {
                      color: {
                          value: materials.color,
                          notes: materials.additionalSpecifications?.color,
                      },
                      shore: {
                          value: materials.shoreValue,
                          notes: materials.additionalSpecifications?.shoreValue,
                      },
                  }
                : '-',
            dfmNotes: isDfmMetricsNotesAvailable
                ? {
                      color: {
                          value: null,
                          notes: materials.dfmNotes?.color,
                      },
                      shore: {
                          value: null,
                          notes: materials.dfmNotes?.shoreValue,
                      },
                  }
                : '-',
            rowSpan: isGeneralMaterialNotesAvailable ? 0 : 1,
        })
    }

    return formatedMaterialPartNotesArray
}

export const isAdditionalAndDfmNotesAvailable = (notes: NotesParameters[]) =>
    notes.some(note => note.additionalNotes !== '-' || note.dfmNotes !== '-')

export const useEnableAutoSchedulePhase3 = () => {
    const identity = useSelector(getUserIdentity)

    return isFlagEnabled(identity, 'ENABLE_AUTO_SCHEDULING_PHASE_3_UI', 'true')
}

export const useEnableIndiaCostingModel = () => {
    const identity = useSelector(getUserIdentity)

    return isFlagEnabled(identity, 'ENABLE_INDIA_COSTING_MODEL_UI', 'true')
}

export const isUserQCManager = (state: DefaultRootState) => {
    const user = getUserIdentity(state)

    return user.isQualityManager
}

export const useTransactionMarginPhase1 = () => {
    const identity = useSelector(getUserIdentity)

    return isFlagEnabled(
        identity,
        'ENABLE_TRANSACTION_MARGIN_PHASE1_UI',
        'true',
    )
}
export const useEnableAutoRoutingUI = () => {
    const identity = useSelector(getUserIdentity)

    return isFlagEnabled(identity, 'ENABLE_AUTO_ROUTING_UI', 'true')
}

export const useIsProcessAvailabilityEnabled = () => {
    const identity = useSelector(getUserIdentity)

    return isFlagEnabled(
        identity,
        'ENABLE_PROCESS_AVAILABILITY_PHASE1_UI',
        'true',
    )
}
